const VERSION_URL = '/_version.json';
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const PROMPT_TYPE_ASSET_ERROR = 'asset-error';
const PROMPT_TYPE_VERSION_CHECK = 'version-check';
const RELOAD_PROMPTS = {
  [PROMPT_TYPE_ASSET_ERROR]: {
    message: 'Some files could not load. Reload to continue.',
    fallbackConfirm: true,
    toastOptions: {
      position: 'top-center',
      className: ['error', 'stale-build-reload-toast', 'stale-build-reload-toast-urgent'],
    },
  },
  [PROMPT_TYPE_VERSION_CHECK]: {
    message: 'A new version is available.',
    fallbackConfirm: false,
    toastOptions: {
      position: 'bottom-right',
      className: ['stale-build-reload-toast', 'stale-build-reload-toast-soft'],
      containerClass: 'stale-build-reload-toast-container',
    },
  },
};

let reloadPromptShown = false;
let lastVersionCheckAt = 0;
let versionCheckPromise = null;

function getErrorText(error) {
  if (!error) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  const parts = [error.name, error.message, error.stack].filter(Boolean);
  return parts.join(' ');
}

function getFailedAssetUrl(error) {
  const target = error && error.target;
  if (!target || !target.tagName) {
    return '';
  }
  const tagName = target.tagName.toLowerCase();
  if (tagName === 'script') {
    return target.src || '';
  }
  if (tagName === 'link') {
    return target.href || '';
  }
  return '';
}

export function isStaleAssetLoadError(error) {
  const text = getErrorText(error).toLowerCase();
  const failedAssetUrl = getFailedAssetUrl(error).toLowerCase();
  if (
    text.includes('common_elements_glossary_mcrwikiglossarycontainer_vue') ||
    failedAssetUrl.includes('common_elements_glossary_mcrwikiglossarycontainer_vue')
  ) {
    return false;
  }

  if (
    text.includes('chunkloaderror') ||
    text.includes('loading chunk') ||
    text.includes('loading css chunk') ||
    text.includes('failed to fetch dynamically imported module') ||
    text.includes('importing a module script failed')
  ) {
    return true;
  }

  return !!failedAssetUrl && failedAssetUrl.includes('/static/') && /\.(js|css)(\?|$)/.test(failedAssetUrl);
}

function reloadPage() {
  try {
    window.location.href = window.location.href; // this should reload without cache
  } catch (e) {
    try {
      window.location = window.location.href; // a fallback
    } catch (e) {
      window.location.reload(); // final fallback to regular reload
    }
  }
}

function createToastOptions(prompt, reload) {
  return {
    duration: null,
    ...prompt.toastOptions,
    action: {
      text: 'Reload',
      onClick: (event, toastObject) => {
        if (toastObject) {
          toastObject.goAway(0);
        }
        reload();
      },
    },
  };
}

function showReloadPrompt(app, promptReload, promptType) {
  const prompt = RELOAD_PROMPTS[promptType] || RELOAD_PROMPTS[PROMPT_TYPE_ASSET_ERROR];
  const toaster = app && app.$toasted;

  if (!promptReload && !toaster && !prompt.fallbackConfirm) {
    return;
  }

  if (reloadPromptShown) {
    return;
  }
  reloadPromptShown = true;

  if (promptReload) {
    promptReload(reloadPage, {
      message: prompt.message,
      type: promptType,
    });
    return;
  }

  if (toaster) {
    toaster.show(prompt.message, createToastOptions(prompt, reloadPage));
    return;
  }

  if (prompt.fallbackConfirm && window.confirm(prompt.message)) {
    reloadPage();
  }
}

function normalizeLatestBuildId(versionInfo) {
  return versionInfo && versionInfo.buildId ? versionInfo.buildId : null;
}

export function createStaleBuildRecovery(options) {
  const router = options.router;
  const app = options.app;
  const currentBuildId = options.currentBuildId;
  const fetchImpl = options.fetchImpl || (window.fetch ? window.fetch.bind(window) : null);
  const versionUrl = options.versionUrl || VERSION_URL;
  const minCheckIntervalMs = options.minCheckIntervalMs || VERSION_CHECK_INTERVAL_MS;
  const promptReload = options.promptReload;

  function promptForReload(promptType) {
    showReloadPrompt(app, promptReload, promptType);
  }

  function checkLatestBuild(force) {
    if (!currentBuildId || !fetchImpl) {
      return Promise.resolve(false);
    }

    const now = Date.now();
    if (!force && now - lastVersionCheckAt < minCheckIntervalMs) {
      return Promise.resolve(false);
    }
    if (versionCheckPromise) {
      return versionCheckPromise;
    }

    lastVersionCheckAt = now;
    versionCheckPromise = fetchImpl(`${versionUrl}?ts=${now}`, {cache: 'no-store'})
      .then(response => {
        if (!response || !response.ok) {
          return false;
        }
        return response.json();
      })
      .then(versionInfo => {
        const latestBuildId = normalizeLatestBuildId(versionInfo);
        if (latestBuildId && latestBuildId !== currentBuildId) {
          promptForReload(PROMPT_TYPE_VERSION_CHECK);
          return true;
        }
        return false;
      })
      .catch(() => false)
      .then(result => {
        versionCheckPromise = null;
        return result;
      });

    return versionCheckPromise;
  }

  function handleError(error) {
    if (isStaleAssetLoadError(error)) {
      promptForReload(PROMPT_TYPE_ASSET_ERROR);
      return true;
    }
    return false;
  }

  function install() {
    if (router && router.onError) {
      router.onError(handleError);
    }
    if (router && router.afterEach) {
      router.afterEach(() => checkLatestBuild(false));
    }
    window.addEventListener('unhandledrejection', event => handleError(event.reason));
    window.addEventListener(
      'error',
      event => {
        handleError(event.error || event);
      },
      true
    );
    window.addEventListener('focus', () => checkLatestBuild(false));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        checkLatestBuild(false);
      }
    });

    checkLatestBuild(true);
  }

  return {
    checkLatestBuild,
    handleError,
    install,
  };
}

export function installStaleBuildRecovery(options) {
  const recovery = createStaleBuildRecovery(options);
  recovery.install();
  return recovery;
}

export function resetStaleBuildRecoveryState() {
  reloadPromptShown = false;
  lastVersionCheckAt = 0;
  versionCheckPromise = null;
}
